import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/libs/utils";
import { CLIP_SCRIM } from "@/constants/gradients";
import { useGame } from "@/api/game-api";
import { useTrans } from "@/utils/useTrans";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";
import SectionHeading from "@/components/home/SectionHeading";
import AudioToHapticDialog from "@/components/home/AudioToHapticDialog";

/**
 * "어떻게 플레이하든, 온몸으로" — 피그마 29032:93777(uc-row-1 / uc-row-2) + 디자이너 공유 HTML.
 *
 * ⚠️ **카드 제목은 국가/언어와 무관하게 영문 고정**이고 부제만 번역된다.
 * 피그마 국문 보드도 6개 전부 영문이다. 한 차례 "국문 유지"로 뒤집혔다 되돌아온 항목이라
 * `t()` 를 통하지 않는다.
 *
 * ⚠️ 영상은 PC·모바일 모두 **상시 재생**(디자이너 확인). 호버 시 재생하는 건 아래 "플레이어 반응"
 * 섹션(`PlayerReactions`)뿐이다. 둘을 섞지 말 것.
 *
 * ⚠️ 호버 오버레이 문구는 **모바일에 없다** — 탭하면 곧바로 해당 페이지로 이동한다(디자이너 확인).
 * `tailwind.config.ts` 의 `future.hoverOnlyWhenSupported` 덕에 터치 기기에서 `hover:` 가 아예
 * 발화하지 않지만, 시안대로 `laptop` 미만에서는 마크업 자체를 감춘다.
 */

/**
 * 홈 `PC Games` 카드의 썸네일 3장 — **기본 상태**의 콘텐츠다(`PcHighlight`).
 *
 * ⚠️ **「호버 안」이 아니다.** `PcHighlight` 는 `UseCase` 안에서 `HoverPanel` **밖**에
 * 놓인다(아래 `{children}` vs `<HoverPanel …/>`). `HoverPanel` 은 `z-[3]` +
 * `bg-[rgba(5,6,8,0.6)]` 라 호버하면 오히려 이 썸네일을 **덮는다.**
 * 노출 폭도 다르다 — 이쪽은 `desktop`(1280), `HoverPanel` 은 `laptop`(1024).
 *
 * ## ★아트는 **admin 우선**, 없을 때만 아래 폴백 (2026-09-03)
 *
 * 종전엔 URL 3개가 통째로 하드코딩이었고 그중 **2개가 Steam CDN 직링크**였다:
 *
 * ```
 *   PUBG          cdn.cloudflare.steamstatic.com/steam/apps/578080/header.jpg
 *   VALORANT      cdn.bhaptics.com/…                    ← 이미 옮겨진 선례
 *   CoD Warzone   cdn.cloudflare.steamstatic.com/steam/apps/1962663/header.jpg
 * ```
 *
 * Steam 쪽은 **우리 통제 밖**이라 게임사가 스토어 아트를 교체하거나 앱 ID 를 옮기면
 * 그날 404 가 되는데 **빌드·테스트에 안 걸린다**(URL 문자열이라 tsc·jest 가 초록).
 *
 * ★**쓰는 필드는 `thumbnailWideLink` 다 — `thumbnailLink` 가 아니다.**
 * 이 타일은 `aspect-[128/72]`(16:9)인데 두 필드의 **비율이 다르다**(2026-09-03 prod):
 *
 * ```
 *                        thumbnailLink                  thumbnailWideLink
 *   pubg-battlegrounds   library_600x900_2x.jpg (2:3)   header.jpg (16:9)
 *   call-of-duty-warzone library_600x900_2x.jpg (2:3)   header.jpg (16:9)
 *   valorant             thumbnail.9a4de0f5….jpg        Val-Console-Launch-Hero….jpg
 * ```
 *
 * `object-cover` 라 세로 아트를 넣으면 **가운데 띠만 남기고 잘린다.** 리포의 가로형
 * 카드가 전부 이 필드를 쓴다(`vr-games-band.tsx:28` 이 같은 판단을 적어 뒀다).
 *
 * ★**「API 로 받으면 해결되나」를 먼저 쟀고, 그것만으로는 안 된다.** 카탈로그에 세
 * 게임이 다 있는데 `thumbnailWideLink` 호스트 분포가 **하드코딩과 똑같다**(prod):
 *
 * ```
 *   pubg-battlegrounds      steamcdn-a.akamaihd.net    /578080/header.jpg
 *   valorant                cdn.bhaptics.com           (아래 폴백과 **동일 URL**)
 *   call-of-duty-warzone    steamcdn-a.akamaihd.net    /1962663/header.jpg
 * ```
 *
 * 그래서 **데이터를 admin 이 고치고**(그 둘의 `thumbnailWideLink` 를 우리 CDN 으로),
 * 코드는 그 값을 그대로 쓰도록 한다. 그러면 admin 이 바꾸는 순간 **코드 변경 없이**
 * 반영되고, 카탈로그를 쓰는 다른 화면(가로형 카드 10곳)도 함께 해결된다.
 *
 * ⚠️ **지금은 화면이 안 바뀐다** — 위 표대로 prod 값이 폴백과 **같은 이미지**다
 * (VALORANT 는 URL 까지 동일, 나머지 둘은 호스트만 다른 같은 `header.jpg`).
 * 이 커밋은 **경로를 여는 것**이고, 실제 교체는 admin 쪽 작업이다.
 *
 * ## 폴백을 남기는 이유
 *
 * 게임이 카탈로그에서 빠지거나(판매중지·비공개) 조회가 실패하면 카드가 **빈 채로**
 * 남는다 — 홈이라 그게 더 나쁘다. 지금 값 그대로를 폴백으로 둔다.
 * `docs/pitfalls.md` #27 — UI 를 갈아치울 때 조용히 사라지는 것을 남기지 않는다.
 */
type FeaturedGame = {
  /** 카탈로그 조회 키. admin 이 소유하는 데이터를 가리킨다. */
  slug: string;
  /** 조회 실패·미등록 시 쓰는 이름. 성공하면 카탈로그 이름을 쓴다. */
  name: string;
  /** 조회 실패·미등록 시 쓰는 이미지. */
  fallbackImage: string;
};

type Card = {
  key: string;
  /**
   * 이동 목적지. `dialog: true` 인 카드는 **이동하지 않으므로** 이 값을 안 읽는다 —
   * 그래도 남긴다(팝업을 되돌릴 때 목적지를 다시 찾지 않게).
   */
  href: string;
  /**
   * ★**이동 대신 팝업을 연다** (#5516 · 작성자 요청).
   *
   * `Console Games` · `Music & Movies` 두 카드가 그렇다. 둘 다 종전 목적지가
   * `/games/audio-to-haptic` 로 **같았고**, 그 페이지의 요지를 팝업이 대신한다.
   */
  dialog?: boolean;
  video: string;
  poster?: string;
  /** 영문 고정 — 번역하지 않는다 */
  title: string;
  subtitle: string;
  hover: { pill: string; heading: string; body: string; cta: string };
  live?: boolean;
};

const PC_CARD: Card = {
  key: "pc",
  href: "/games/pc",
  video: "https://cdn.bhaptics.com/media/01_PC_640p.mp4",
  title: "PC Games",
  subtitle: "Feel PC game sound and action",
  hover: {
    pill: "PC GAMES",
    heading: "Make PC Games Feel More Real",
    body: "AI turns game sound and action into real-time haptics, so you can feel every in-game moment through your body.",
    cta: "Explore PC Games",
  },
};

const VR_CARD: Card = {
  key: "vr",
  /**
   * ★**VR Games Overview 로 보낸다** — 종전엔 `/games/meta-quest` 였다
   * (#5517 · 작성자 요청).
   *
   * 그 경로는 **Quest 전용 랜딩**이다(실측 2026-09-04: h1 「Wireless Haptics for the
   * Ultimate Quest Experience」). 카드 제목이 `VR Games` 인데 목적지가 헤드셋 한 종류로
   * 좁혀져 있었다.
   *
   * `/games/vr` 이 리뉴얼된 Overview 다(「From seeing the game to feeling the game」).
   * 옆의 PC 카드는 이미 `/games/pc` 라, 이 변경으로 **두 카드가 같은 층위**가 된다.
   *
   * ⚠️ `/games/meta-quest` 페이지는 그대로 둔다 — 이 카드의 진입만 옮긴 것이다.
   */
  href: "/games/vr",
  video: "https://cdn.bhaptics.com/media/02_VR_640p.mp4",
  title: "VR Games",
  subtitle: "Feel every VR game moment",
  hover: {
    pill: "VR GAMES",
    heading: "Play Every VR Game in Haptics",
    body: "Audio Capture turns sound from every VR game into haptic feedback, so you can enjoy more VR titles in a more physical way.",
    cta: "Explore VR Games",
  },
};

const ROW_2: Card[] = [
  {
    key: "console",
    href: "/games/vr",
    dialog: true,
    video: "https://cdn.bhaptics.com/media/04_Console_640p.mp4",
    title: "Console Games",
    subtitle: "Make console games feel more real",
    hover: {
      pill: "CONSOLE GAMES",
      heading: "Bring Haptics to Console Play",
      body: "Audio Capture turns console game sound into haptic feedback, delivering in-game action through your body.",
      cta: "Learn More",
    },
  },
  {
    key: "sim-racing",
    /**
     * ★**PC Games Overview 로 보낸다** — 종전엔 `/games/sim-racing` 이었다
     * (#5517 · 작성자 요청).
     *
     * ## ⚠️ 착지 화면에 심레이싱 맥락이 없다 — 알고 한 결정이다 (#5542 R1 n1)
     *
     * 이 카드는 `SIM RACING` 배지와 「엔진·노면·충돌」을 말한 뒤 보내는데,
     * `/games/pc` 에는 그 주제가 **하나도 없다**(실측 2026-09-04 dev):
     *
     * ```
     *   본문 "sim racing" · "심레이싱"      0건
     *   sim-racing 으로 가는 링크           0개
     *   서브내비(GamesSubNav)               Overview · PC Games · Software
     * ```
     *
     * 즉 이 카드로 들어온 사용자는 **착지 후 심레이싱으로 갈 방법이 없다.**
     * 남은 진입점은 **모바일 GNB 시트뿐**이다 — `GAMES_SUBMENUS`(`navigation-menus.ts`
     * 의 `Sim Racing`)를 `menu-view` 가 읽는다. **데스크탑 GNB 에는 없다**(그 파일
     * 주석: 「아래 페이지들이 GNB 진입점을 잃는다 … /games/sim-racing」).
     *
     * 그래도 이 목적지가 작성자 결정이다(2026-09-04). CTA 를 `Explore Sim Racing` →
     * 일반 문구로 바꾼 것이 그 대가를 줄이는 부분이고, **본문(pill·heading·body)은
     * 여전히 심레이싱을 말한다** — 착지 화면에 그 섹션이 생기면 앵커로 보내는 것이
     * 다음 수순이다.
     */
    href: "/games/pc",
    video: "https://cdn.bhaptics.com/media/03_SimRacing_640p.mp4",
    title: "Sim Racing",
    subtitle: "Feel engines, roads, and collisions",
    hover: {
      pill: "SIM RACING",
      heading: "Feel Engines, Roads, and Collisions",
      body: "Engine sounds, road texture, and collisions are delivered as haptic feedback for a more realistic driving experience.",
      /**
       * ★**형제와 달리 목적지를 약속하지 않는 문구다** — 이 카드만 목적지가
       * 심레이싱 전용 페이지가 아니라 **PC Games Overview** 이기 때문이다(#5517).
       *
       * 형제는 `Explore VR Games`·`Explore Audio-to-Haptics` 처럼 목적지를 이름으로
       * 부르는데, 여기서 `Explore Sim Racing` 을 그대로 두면 **누르면 심레이싱
       * 페이지가 나올 것으로 읽힌다.**
       *
       * ## ★키 결정이 한 번 번복됐다 — 지금은 **공유 키를 재사용**한다
       *
       * ```
       *   2026-09-04  전용 키 `Find Out More` 신설   kr 「자세히 알아보기」
       *   2026-09-07  #5595 「Learn more 로 변경」    kr 「더 알아보기」        ← 지금
       * ```
       *
       * 종전 주석은 *"기존 키를 재사용하지 않았다 — 한글이 다르다"* 를 근거로 적고
       * 있었는데, #5595 가 그 결정을 뒤집었다. 전용 키는 걷었고(7로케일) 리포가 이미
       * 11곳에서 쓰는 `Learn More` 로 돌아왔다.
       *
       * ⚠️ **한글도 함께 바뀐다** — 「자세히 알아보기」는 이 변경으로 번역 파일에서
       * 완전히 사라졌다(전수 0건). 영문만 바꾸고 한글을 유지하려면 키 이름과 값이
       * 어긋나므로(이 리포는 **키 = 영문 원문**) 그 선택지는 없다.
       *
       * 화살표(`>`)는 이 컴포넌트가 SVG 로 이미 그린다.
       */
      cta: "Learn More",
    },
  },
  {
    key: "music-movies",
    href: "/games/vr",
    dialog: true,
    video: "https://cdn.bhaptics.com/media/05_Music_640p.mp4",
    title: "Music & Movies",
    subtitle: "Turn sound into a physical experience",
    hover: {
      pill: "MUSIC & MOVIES",
      heading: "Feel Sound Through Your Body",
      body: "Music and movie sound is turned into haptics, creating a new way to experience what you hear.",
      cta: "Learn More",
    },
  },
  {
    key: "streaming",
    href: "/software/live",
    // 세로 영상이라 cover 가 위아래를 자르고 중앙 정렬한다 — 검은 띠 대신 잘리는 쪽(기획 요청).
    video: "/videos/home/streaming.mp4",
    poster: "/images/software/live/simulation-thumbnail.jpg",
    title: "Streaming",
    subtitle: "Turn reactions into streaming content",
    live: true,
    hover: {
      pill: "STREAMING",
      heading: "Turn Reactions into Content",
      body: "bHaptics Live connects viewer support and live events to haptic feedback, making your stream more interactive.",
      cta: "Explore bHaptics Live",
    },
  },
];

const FEATURED_GAMES: FeaturedGame[] = [
  {
    slug: "pubg-battlegrounds",
    name: "PUBG",
    fallbackImage:
      "https://cdn.cloudflare.steamstatic.com/steam/apps/578080/header.jpg",
  },
  {
    slug: "valorant",
    name: "VALORANT",
    fallbackImage:
      "https://cdn.bhaptics.com/images/game-assets/VALORANT/Val-Console-Launch-Hero-1080-__-GAME-STORES-80b474d3ff38615edd0a.jpg",
  },
  {
    slug: "call-of-duty-warzone",
    name: "Call of Duty",
    fallbackImage:
      "https://cdn.cloudflare.steamstatic.com/steam/apps/1962663/header.jpg",
  },
];

export default function PlayEveryWay({ className }: { className?: string }) {
  const { t } = useTrans(["index"]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});
  /**
   * Audio-to-Haptics 팝업 (#5516) — **상태는 여기 하나**다.
   *
   * `Console Games` · `Music & Movies` 두 카드가 같은 팝업을 연다. 카드 안에 상태를
   * 두면 다이얼로그가 두 벌 생기고 어느 것이 열렸는지 상위에서 알 수 없다
   * (`docs/pitfalls.md` #7).
   */
  const [athOpen, setAthOpen] = useState(false);

  return (
    // 히어로의 스크롤 힌트(#usecase)가 이 섹션으로 내려온다.
    <section id="usecase" className={cn("w-full px-4 laptop:px-8", className)}>
      <div className="mx-auto w-full max-w-screen-max">
        <SectionHeading
          title={t("Feel the Game Across Every Way You Play")}
          description={t(
            "TactSuit brings haptic feedback to every way you play.",
          )}
        />

        <div ref={ref} className="mt-8 flex flex-col gap-4 laptop:gap-6">
          {/* 1행 — PC(952) + VR(464). laptop 이상은 시안 비율(1440x560)을 유지해 폭에 따라 같이 줄어든다. */}
          <div className="grid grid-cols-1 gap-4 laptop:aspect-[1440/560] laptop:grid-cols-[952fr_464fr] laptop:gap-6">
            <UseCase
              card={PC_CARD}
              size="lg"
              style={{ transitionDelay: delayFunc(0) }}
              className={delayClassName}
            >
              <PcHighlight />
            </UseCase>
            <UseCase
              card={VR_CARD}
              size="md"
              style={{ transitionDelay: delayFunc(1) }}
              className={delayClassName}
            />
          </div>

          {/* 2행 — 콘솔 · 심레이싱 · 뮤직/무비 · 스트리밍 */}
          <div className="grid grid-cols-2 gap-4 laptop:aspect-[1440/372] laptop:grid-cols-4 laptop:gap-6">
            {ROW_2.map((card, index) => (
              <UseCase
                key={card.key}
                card={card}
                size="sm"
                style={{ transitionDelay: delayFunc(index + 2) }}
                className={delayClassName}
                onDialog={() => setAthOpen(true)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Audio-to-Haptics 팝업 — 두 카드가 공유한다(#5516). */}
      <AudioToHapticDialog open={athOpen} onOpenChange={setAthOpen} />
    </section>
  );
}

/** 시안의 3가지 크기 티어. 호버 오버레이의 패딩·타이포가 여기에 딸려 온다. */
type Size = "lg" | "md" | "sm";

function UseCase({
  card,
  size,
  className,
  style,
  onDialog,
  children,
}: React.PropsWithChildren<{
  card: Card;
  size: Size;
  className?: string;
  style?: React.CSSProperties;
  /** `card.dialog` 인 카드를 눌렀을 때. 부모가 팝업 상태를 갖는다. */
  onDialog?: () => void;
}>) {
  const { t } = useTrans(["index"]);
  const tall = size !== "sm";

  const shell = cn(
    "group relative block overflow-hidden rounded-2xl bg-[#161616]",
    // laptop 이상은 행의 aspect-ratio 가 높이를 정하므로 h-full 로 늘린다.
    tall ? "h-[320px]" : "h-[240px]",
    "laptop:h-full",
    className,
  );

  /** 껍데기(`<Link>`/`<button>`)가 공유하는 내용. 갈리는 것은 바깥 태그뿐이다. */
  const body = (
    <>
      {/* ⚠️ `LazyLoadVideo` 를 쓰면 안 된다. 화면 밖으로 나갈 때 `<source>` 를 떼어내고
          다시 들어올 때 붙이는데, 브라우저가 자원 재선택을 하지 않아 **정지한 채로 남는다**.
          (dev 실측: 지나쳤다 돌아오면 6개 전부 paused=true) 평범한 video + 정적 source 로 둔다. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        poster={card.poster}
        className="absolute inset-0 size-full object-cover"
      >
        <source src={card.video} type="video/mp4" />
      </video>
      <div
        className="absolute inset-0"
        style={{ backgroundImage: CLIP_SCRIM }}
        aria-hidden
      />

      {/* LIVE 배지 위치가 모바일(20/18)과 데스크톱(14/14)에서 다르다. */}
      {card.live && (
        <span className="absolute left-5 top-[18px] z-[2] rounded-full bg-[#FF3D3D] px-2.5 py-1 text-2xs font-700 leading-none text-white laptop:left-3.5 laptop:top-3.5">
          LIVE
        </span>
      )}

      {children}

      {/* 시안: 라벨은 카드 좌측 20px. 바닥 여백은 모바일 18px,
          데스크톱은 17px(560 카드) / 13px(372 카드). */}
      <div
        className={cn(
          "absolute inset-x-5 z-[2] flex flex-col gap-1 text-white",
          "bottom-[18px]",
          tall ? "laptop:bottom-[17px]" : "laptop:bottom-[13px]",
        )}
      >
        {/* ⚠️ 제목은 번역하지 않는다 (피그마 국문 보드도 영문) */}
        <h3 className="text-xl font-600 laptop:text-2xl laptop:font-400">
          {card.title}
        </h3>
        <p className="text-sm tracking-[0.4px]">{t(card.subtitle)}</p>
      </div>

      <HoverPanel card={card} size={size} />
    </>
  );

  /**
   * ★**팝업 카드는 `<button>` 이다** (#5516).
   *
   * `<Link>` 로 두고 `preventDefault` 하면 **주소가 보이는데 안 가는** 링크가 된다 —
   * 새 탭으로 열기·주소 복사가 페이지로 새고, 스크린리더는 「링크」로 읽는다.
   * 여는 동작이므로 버튼이 맞다.
   *
   * ⚠️ 팝업 상태는 여기 두지 않는다 — `PlayEveryWay` 가 갖는다. 카드 안에 두면
   * 다이얼로그가 카드 수만큼 생기고, 어느 것이 열렸는지 상위에서 알 수 없다
   * (`docs/pitfalls.md` #7 — 형제 다이얼로그는 상태를 부모로 끌어올린다).
   */
  if (card.dialog) {
    return (
      <button
        type="button"
        onClick={onDialog}
        aria-haspopup="dialog"
        aria-label={t(card.hover.cta)}
        style={style}
        // `<button>` 기본 가운데 정렬을 끈다 — 링크판과 같은 배치를 유지한다.
        className={cn(shell, "w-full text-left")}
      >
        {body}
      </button>
    );
  }

  return (
    <Link
      href={card.href}
      aria-label={t(card.hover.cta)}
      style={style}
      className={shell}
    >
      {body}
    </Link>
  );
}

/**
 * 호버 오버레이 — 피그마 `hover-state`(lg 29032:93707 / md 29032:93719 / sm 29032:93732).
 * 모바일은 탭 → 바로 이동이라 마크업 자체를 두지 않는다.
 *
 * 카드 크기별 3티어. 배경(rgba(5,6,8,.6))과 배지는 공통이고 여백·타이포·셰브론만 달라진다.
 *   lg  px 40 · gap 14 · 28/36 · 본문 15/22(폭 420 고정) · CTA 15/22 · 셰브론 18
 *   md  px 32 · gap 12 · 22/28 · 본문 14/18             · CTA 14/18 · 셰브론 17
 *   sm  px 24 · gap 10 · 20/26 · 본문 13/18             · CTA 13/18 · 셰브론 16
 *
 * ⚠️ 본문 폭 제한은 **lg 에만** 있다(420px). md/sm 은 카드 폭을 그대로 쓴다 — 시안 메타의
 * 400/294 는 카드 폭에서 좌우 패딩을 뺀 값이지 제한이 아니다. max-width 를 주면 카드가
 * 좁아질 때 줄바꿈 위치가 시안과 어긋난다.
 */
function HoverPanel({ card, size }: { card: Card; size: Size }) {
  const { t } = useTrans(["index"]);

  return (
    <div
      className={cn(
        "absolute inset-0 z-[3] hidden flex-col items-start justify-center rounded-2xl bg-[rgba(5,6,8,0.6)]",
        // 들어올 때·나갈 때 모두 부드럽게.
        //
        // ⚠️ 이 repo 에서 `duration-` · `delay-` · `ease-` 는 **테마 스케일 값만** 받는다.
        // `tailwindcss-animate`(tailwind.config.ts `plugins`)가 이 셋을 `matchUtilities` 로
        // `animation*` 속성에 재등록하면서 core 의 임의값 지원을 가린다.
        // 실측: 플러그인이 있으면 `duration-[250ms]`·`delay-[120ms]`·`ease-[linear]` 가
        // 생성되지 않고, `plugins: []` 로만 바꾸면 셋 다 생성된다.
        // 스케일 값(`duration-75`·`duration-700` 등)은 정상이고, 다른 임의값
        // (`size-[18px]`·`tracking-[.25px]`)도 정상이다 — 이 셋만의 문제다.
        //
        // 클래스는 DOM 에 붙지만 규칙이 없어 `transition-opacity` 기본값 150ms 로 조용히
        // 돌아간다. 스케일 밖 값이 필요하면 `theme.extend.transitionDuration` 에 추가할 것
        // (같은 한계를 우회한 선례: `theme.extend.transitionDelay = { 400: "400ms" }`).
        "opacity-0 transition-opacity duration-300 ease-out",
        "group-hover:opacity-100 group-focus-visible:opacity-100",
        "laptop:flex",
        size === "lg" && "gap-3.5 px-10",
        size === "md" && "gap-3 px-8",
        size === "sm" && "gap-2.5 px-6",
      )}
    >
      <span className="rounded-full bg-white/[0.16] px-3 py-1.5 text-xs font-500 tracking-[0.5px] text-white">
        {card.hover.pill}
      </span>
      <p
        className={cn(
          "font-400 text-white",
          size === "lg" && "text-4xl",
          size === "md" && "text-2xl",
          size === "sm" && "text-[20px] leading-[26px]",
        )}
      >
        {t(card.hover.heading)}
      </p>
      <p
        className={cn(
          "tracking-[0.25px] text-[#E0E0E0]",
          size === "lg" && "max-w-[420px] text-[15px] leading-[22px]",
          size === "md" && "text-base",
          size === "sm" && "text-[13px] leading-[18px]",
        )}
      >
        {t(card.hover.body)}
      </p>
      <span
        className={cn(
          "inline-flex items-center font-600 tracking-[0.25px] text-bhaptics-yellow",
          size === "lg" && "text-[15px] leading-[22px]",
          size === "md" && "text-base",
          size === "sm" && "text-[13px] leading-[18px]",
        )}
      >
        {t(card.hover.cta)}
        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden
          className={cn(
            "shrink-0",
            size === "lg" && "size-[18px]",
            size === "md" && "size-[17px]",
            size === "sm" && "size-4",
          )}
        >
          <path
            d="M8.4 6.2L12.2 10L8.4 13.8"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
}

/**
 * 썸네일 한 장 — **admin(카탈로그) 우선 · 없으면 폴백** (위 `FeaturedGame` 주석).
 *
 * ★**카드마다 컴포넌트를 나눈다.** `useGame(slug)` 을 부모에서 `map` 으로 부르면 훅을
 * 반복 호출하게 되어 훅 규칙(호출 순서 고정)을 깬다 — 목록이 3으로 고정이라 지금은
 * 우연히 동작하지만, 게임이 늘거나 조건부로 그리는 순간 깨진다. 자식으로 나누면
 * 컴포넌트당 훅이 **정확히 하나**다.
 *
 * ⚠️ 이름도 카탈로그 것을 쓰지 **않는다** — 카탈로그는 `Call of Duty®: Warzone™` 처럼
 * 상표 기호가 붙은 정식명이라 128px 타일에서 두 줄로 넘친다. 여기 이름은 **디자인이
 * 고른 짧은 표기**이므로 코드가 소유한다(아트만 admin 이 소유).
 *
 * ★**`enabled` 가 필수다.** 이 타일을 담은 `PcHighlight` 래퍼가 `hidden … desktop:flex`
 * 라 1280 미만에서는 **화면에 없는데 마운트는 된다.** 게이트가 없으면 모바일·태블릿
 * 방문자가 홈 로드마다 안 보이는 콘텐츠를 위해 `by-slug` 3건(실측 2026-09-03 dev
 * **7,516 B**)을 받는다. 홈은 이 사이트에서 노출이 가장 큰 페이지다.
 * 판정은 부모가 한 번만 하고 내려준다 — 타일마다 `matchMedia` 리스너를 달지 않는다.
 */
function FeaturedGameTile({
  game,
  enabled,
}: {
  game: FeaturedGame;
  enabled: boolean;
}) {
  const { data } = useGame(game.slug, { enabled });
  // `||` 로 폴백 — `??` 면 빈 문자열이 통과해 `<Image src="">` 가 된다. 이 필드는
  // 실제로 비어 있을 수 있어서(`vr-games-band.tsx:69` 가 같은 이유로 필터링한다)
  // 「없음」 표기가 `null` 하나가 아니다.
  const image = data?.thumbnailWideLink || game.fallbackImage;

  return (
    <figure className="m-0 flex flex-col gap-[5px]">
      <div className="relative aspect-[128/72] w-full overflow-hidden rounded-lg">
        <Image
          src={image}
          alt={game.name}
          fill
          sizes="128px"
          className="object-cover"
        />
      </div>
      <figcaption className="text-sm tracking-[0.4px]">{game.name}</figcaption>
    </figure>
  );
}

/**
 * PC 카드 안의 AI 햅틱 소개 + 대표 게임 — 피그마 29032:93786.
 *
 * ⚠️ `desktop`(1280) 미만에서는 감춘다. 카드가 폭에 비례해 줄어드는데 이 패널의 타이포는
 * 고정이라, laptop(1024) 구간에서는 카드 높이(≈386px)보다 패널이 더 길어져 라벨을 덮는다.
 * 썸네일은 고정 128px 이 아니라 3등분 + `aspect-[128/72]` 로 둬야 1280~1440 사이에서 넘치지 않는다.
 */
function PcHighlight() {
  const { t } = useTrans(["index"]);
  /**
   * ★아래 래퍼의 `desktop:flex` 와 **같은 값**이다(tailwind `desktop` = 1280px).
   * 둘이 갈리면 안 보이는데 받거나 보이는데 폴백만 나온다 — 바꿀 때 같이 바꾼다.
   * `useMediaQuery` 는 마운트 전 `false` 라 SSR/hydration 이 어긋나지 않는다.
   */
  const isVisible = useMediaQuery("(min-width: 1280px)");

  return (
    <div className="pointer-events-none absolute bottom-[4.29%] left-[55.04%] z-[2] hidden w-[42.44%] flex-col gap-[18px] text-white desktop:flex">
      <div className="flex flex-col gap-2.5">
        <span className="w-fit rounded-full bg-white/[0.18] px-3 py-1.5 text-xs tracking-[0.5px]">
          AI POWERED HAPTICS
        </span>
        <p className="text-xl font-600 tracking-[0.1px]">
          {t("Games Powered by AI POWERED Haptics")}
        </p>
        <p className="text-base tracking-[0.25px] text-[#EBEBEB]">
          {t(
            "AI turns gunshots, explosions, and collisions into haptics. Feel popular games with TactSuit.",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-600 tracking-[0.4px]">
          {t("Featured Games")}
        </p>
        <div className="grid grid-cols-3 gap-2.5">
          {FEATURED_GAMES.map((game) => (
            <FeaturedGameTile key={game.slug} game={game} enabled={isVisible} />
          ))}
        </div>
      </div>
    </div>
  );
}
